import { agentWritesApi } from '../../api/agents/agent-writes'
import { sightingsApi } from '../../api/sightings'
import { invalidateGraphData } from '../../api/cache-coherence/graph-data-invalidation'
import type { InFlightAgentActionProposal } from '../../slices/agent-orchestrator-slice'
import { scheduleHydratePendingAgentWrites } from '../cypher-write-pending'
import {
  readStructuredMutationError,
  registerAgentActionLifecycle,
  type AgentActionApplyOutcome,
  type AgentActionCompleteHydration,
  type AgentActionLifecycleCapability,
  type AgentActionRejectOutcome
} from './lifecycle-registry'

function readString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key]
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function readNumber(record: Record<string, unknown>, key: string): number | undefined {
  const value = record[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function readStringArray(record: Record<string, unknown>, key: string): string[] {
  const value = record[key]
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : []
}

function readPendingOutput(
  parentToolUseId: string,
  output: unknown
): AgentActionCompleteHydration | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) return null
  const record = output as Record<string, unknown>
  if (record.pending !== true) return null
  const planId = readString(record, 'plan_id')
  if (!planId) return null
  const writeIds = readStringArray(record, 'write_ids')
  return {
    parentToolUseId,
    planId,
    results: writeIds.map((writeId) => ({
      toolUseId: parentToolUseId,
      writeId
    }))
  }
}

function apply(
  toolUseId: string,
  options?: { branchId?: string }
): ReturnType<AgentActionLifecycleCapability['apply']> {
  return async (dispatch): Promise<AgentActionApplyOutcome> => {
    try {
      const result = await dispatch(
        agentWritesApi.endpoints.applyCypherWrite.initiate({
          toolUseId,
          graphAffecting: true,
          ...(options?.branchId ? { branchId: options.branchId } : {})
        })
      ).unwrap()
      if (!result.ok) {
        return { kind: 'apply_failed', error: result.error ?? 'Apply failed.' }
      }
      invalidateGraphData(dispatch)
      dispatch(
        sightingsApi.util.invalidateTags([
          { type: 'ConditionApplicability', id: 'LIST' },
          { type: 'Sighting', id: 'LIST' }
        ])
      )
      return { kind: 'success' }
    } catch (error) {
      const refused = readStructuredMutationError(error)
      if (refused) {
        return { kind: 'apply_failed', error: refused.error }
      }
      return {
        kind: 'transport_error',
        error: error instanceof Error ? error.message : 'Apply failed.'
      }
    }
  }
}

function reject(
  toolUseId: string,
  reason: string
): ReturnType<AgentActionLifecycleCapability['reject']> {
  return async (dispatch): Promise<AgentActionRejectOutcome> => {
    try {
      const result = await dispatch(
        agentWritesApi.endpoints.rejectCypherWrite.initiate({
          toolUseId,
          reason
        })
      ).unwrap()
      return result.ok
        ? {
            kind: 'success',
            settledMeta: { rejectionReason: result.reason ?? reason }
          }
        : { kind: 'reject_failed', error: result.error ?? 'Reject failed.' }
    } catch (error) {
      return {
        kind: 'transport_error',
        error: error instanceof Error ? error.message : 'Reject failed.'
      }
    }
  }
}

function terminalLine(
  proposal: InFlightAgentActionProposal,
  displayNumber: number,
  appliedSuffix: string
): string {
  const terminal = proposal.terminalState
  if (terminal?.kind === 'applied') {
    return `${displayNumber}. APPLIED — "${proposal.summary}"; ${appliedSuffix}.`
  }
  if (terminal?.kind === 'apply_failed') {
    return `${displayNumber}. APPLY_FAILED — "${proposal.summary}"; error: ${terminal.error}.`
  }
  if (terminal?.kind === 'rejected') {
    return `${displayNumber}. REJECTED — "${proposal.summary}".`
  }
  return `${displayNumber}. UNDECIDED — "${proposal.summary}".`
}

export const conditionVocabularyLifecycleCapability: AgentActionLifecycleCapability = {
  kind: 'condition_vocabulary',
  toolName: 'propose_condition',
  parseToolStart(toolUseId, input, startedAtMs) {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return []
    const record = input as Record<string, unknown>
    const name = readString(record, 'name')
    const conditionKind = readString(record, 'kind') ?? 'other'
    if (!name) return []
    const slug = name
      .normalize('NFKD')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
    if (!slug) return []
    const equipmentType = readString(record, 'equipment_type') ?? ''
    return [
      {
        id: toolUseId,
        proposal: {
          kind: 'condition_vocabulary',
          conditionId: `urn:condition:${conditionKind}/${slug}`,
          name,
          conditionKind,
          perceivableBy: readStringArray(record, 'perceivable_by'),
          equipmentType,
          howToCheck: readString(record, 'how_to_check') ?? '',
          summary: `Add ${name} inspection for ${equipmentType}`,
          startedAtMs
        }
      }
    ]
  },
  parseToolComplete: readPendingOutput,
  scheduleHydratePending: scheduleHydratePendingAgentWrites,
  apply,
  reject,
  buildResumeRowLine(proposal, displayNumber) {
    return terminalLine(proposal, displayNumber, 'condition and applicability updated')
  }
}

export const indicatesLifecycleCapability: AgentActionLifecycleCapability = {
  kind: 'indicates',
  toolName: 'propose_indicator',
  parseToolStart(toolUseId, input, startedAtMs) {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return []
    const record = input as Record<string, unknown>
    const instrumentId = readString(record, 'instrument_id')
    const observableId = readString(record, 'observable_id')
    if (!instrumentId || !observableId) return []
    const display = readString(record, 'display')
    const rangeLow = readNumber(record, 'range_low')
    const rangeHigh = readNumber(record, 'range_high')
    const source = readString(record, 'source')
    return [
      {
        id: toolUseId,
        proposal: {
          kind: 'indicates',
          instrumentId,
          observableId,
          ...(display ? { display } : {}),
          ...(rangeLow !== undefined ? { rangeLow } : {}),
          ...(rangeHigh !== undefined ? { rangeHigh } : {}),
          ...(source ? { source } : {}),
          summary: `Link instrument ${instrumentId} to observable ${observableId}`,
          startedAtMs
        }
      }
    ]
  },
  parseToolComplete: readPendingOutput,
  scheduleHydratePending: scheduleHydratePendingAgentWrites,
  apply,
  reject,
  buildResumeRowLine(proposal, displayNumber) {
    return terminalLine(proposal, displayNumber, 'indicator mapping updated')
  }
}

registerAgentActionLifecycle(conditionVocabularyLifecycleCapability)
registerAgentActionLifecycle(indicatesLifecycleCapability)
